/**
 * Review card (snippets/c-cart-proof.liquid): a tap anywhere on the card shows the next quote.
 * In the cart drawer it is user-driven only. On the PDP (data-autoplay) it also moves on every 5s,
 * held while hovered, focused, off screen or in a background tab; the first tap stops it for good,
 * so the tap is the pause (WCAG 2.2.2). Reduced motion never autoplays.
 *
 * Loaded by snippets/cart-drawer.liquid (a Roots edit) rather than the snippet, because a script
 * inside a re-rendered drawer does not execute; blocks/c-lv2-buy.liquid loads it for the PDP. A
 * re-render resets the card to the first quote.
 */
const INTERVAL = 5000;

class CartProof extends HTMLElement {
  #timer = 0;
  #held = new Set();
  #io;

  connectedCallback() {
    this.querySelector('[data-next]')?.addEventListener('click', this.#tap);
    if (!this.hasAttribute('data-autoplay') || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Silent while it turns by itself, so a screen reader is not read a quote every 5s.
    this.querySelector('[aria-live]')?.setAttribute('aria-live', 'off');
    const hold = (/** @type {string} */ why, /** @type {boolean} */ on) => {
      on ? this.#held.add(why) : this.#held.delete(why);
      this.#schedule();
    };
    this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && hold('hover', true));
    this.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && hold('hover', false));
    this.addEventListener('focusin', () => hold('focus', true));
    this.addEventListener('focusout', () => hold('focus', false));
    document.addEventListener('visibilitychange', () => hold('tab', document.hidden));
    this.#io = new IntersectionObserver(([en]) => hold('away', !en.isIntersecting), { threshold: 0.5 });
    this.#io.observe(this);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#io?.disconnect();
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.#io && !this.#held.size) this.#timer = setTimeout(() => (this.#next(), this.#schedule()), INTERVAL);
  }

  #tap = () => {
    if (this.#io) {
      this.#io.disconnect();
      this.#io = undefined;
      clearTimeout(this.#timer);
      this.querySelector('[aria-live]')?.setAttribute('aria-live', 'polite');
    }
    this.#next();
  };

  #next() {
    const reviews = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-review]')]);
    const current = reviews.findIndex((review) => !review.hidden);
    const next = (current + 1) % reviews.length;
    reviews[current].hidden = true;
    reviews[next].hidden = false;
    const pos = this.querySelector('[data-pos]');
    if (pos) pos.textContent = String(next + 1);
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
      reviews[next].animate(
        [
          { opacity: 0.15, transform: 'translateY(4px)' },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 340, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    }
  }
}

if (!customElements.get('c-cart-proof')) customElements.define('c-cart-proof', CartProof);
